import type { Question, QuestionResult } from "@/lib/api-types";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface QuizCardProps {
	question: Question;
	questionIndex: number;
	totalQuestions: number;
	selectedAnswers: string[];
	onSelect: (optionId: string) => void;
	result?: QuestionResult;
	disabled?: boolean;
}

export function QuizCard({
	question,
	questionIndex,
	totalQuestions,
	selectedAnswers,
	onSelect,
	result,
	disabled,
}: QuizCardProps) {
	const { t } = useTranslation();
	const isMultiple = question.type === "multiple_choice";
	const isSubmitted = !!result;

	return (
		<div className="rounded-xl border border-white/10 bg-bg-elevated p-6">
			<p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted">
				{t("quiz.question", {
					current: questionIndex + 1,
					total: totalQuestions,
				})}
			</p>
			<p className="mb-6 text-lg font-medium text-primary">{question.text}</p>

			<div className="space-y-2">
				{question.options.map((option) => {
					const isSelected = selectedAnswers.includes(option.id);
					const isCorrectAnswer =
						isSubmitted &&
						(Array.isArray(result.correct_answer)
							? result.correct_answer.includes(option.id)
							: result.correct_answer === option.id);
					const isWrongSelection = isSubmitted && isSelected && !result.correct;

					return (
						<button
							key={option.id}
							type="button"
							disabled={disabled || isSubmitted}
							onClick={() => onSelect(option.id)}
							className={cn(
								"flex w-full items-center gap-3 rounded-lg border-l-4 px-4 py-3 text-left text-sm transition-colors duration-150",
								// Default state
								!isSelected &&
									!isSubmitted &&
									"border-l-transparent bg-bg-overlay text-secondary hover:bg-bg-hover hover:text-primary",
								// Selected (before submit)
								isSelected && !isSubmitted && "border-l-accent bg-accent-muted text-primary",
								// After submit: correct
								isCorrectAnswer && "border-l-success bg-success-muted text-primary",
								// After submit: wrong selection
								isWrongSelection && "border-l-error bg-error-muted text-primary",
								// After submit: neither
								isSubmitted &&
									!isCorrectAnswer &&
									!isWrongSelection &&
									"border-l-transparent bg-bg-overlay text-muted",
								(disabled || isSubmitted) && "cursor-default",
							)}
						>
							<span
								className={cn(
									"flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border text-xs",
									isSelected || isCorrectAnswer
										? "border-accent bg-accent text-white"
										: "border-muted",
									isMultiple && "rounded",
								)}
							>
								{isSelected && !isMultiple && "●"}
								{isSelected && isMultiple && "✓"}
							</span>
							<span>{option.text}</span>
						</button>
					);
				})}
			</div>

			{isSubmitted && result.explanation && (
				<div className="mt-4 rounded-lg bg-bg-overlay p-4 text-sm text-secondary">
					{result.explanation}
				</div>
			)}
		</div>
	);
}
